import * as styles from "./RehomingCentreKeyDetails.module.scss";

import React, { useState } from "react";
import { Button, SocialMediaIcons } from "../../../components/common";
import {
	Clock,
	Envelope,
	HearingLoop,
	Location,
	Phone,
	Wheelchair,
} from "../../../components/common/Icons";
import { Colours } from "../../../utils/colours";
import WrappedSection from "../../WrappedSection/WrappedSection";

const DEFAULT_OPENING_TIME_TEXT =
	"We're open for general browsing on Saturday, Sunday, Tuesday and Thursday from 12 - 4pm.";
const DEFAULT_WHEELCHAIR_TEXT =
	"This centre provides easy wheelchair access throughout";

const DEFAULT_PHONE_LINES_TEXT_L1 =
	"Phone lines open everyday from 8.30am to 5pm";

const DEFAULT_HEARING_TEXT = "Please ask at reception";

export interface SectionRehomingCentreKeyDetailsProps {
	heading: string;
	addressLineOne: string;
	addressLineTwo: string;
	city: string;
	county: string;
	postcode: string;
	openingTimesText: string;
	phoneNumber: string;
	googleMapsLink: string;
	facebookUrl: string;
	instagramUrl: string;
	xUrl: string;
	linkedinUrl: string;
	wheelchairText: string;
	isVirtualCentre: boolean;
}

export const getRehomingCentreKeyDetailsProps = ({
	title,
	field_address_line_one,
	field_address_line_two,
	field_city,
	field_county,
	field_postcode,
	field_opening_times_text,
	field_phone_number,
	field_google_maps_link,
	field_facebook_url,
	field_instagram_url,
	field_twitter_url,
	field_linkedin_url,
	field_wheelchair_text,
	field_is_virtual_centre,
}: Queries.RehomingCentreDataFragment) => ({
	heading: title,
	addressLineOne: field_address_line_one,
	addressLineTwo: field_address_line_two,
	city: field_city,
	county: field_county,
	postcode: field_postcode,
	openingTimesText: field_opening_times_text,
	phoneNumber: field_phone_number,
	googleMapsLink: field_google_maps_link,
	facebookUrl: field_facebook_url,
	instagramUrl: field_instagram_url,
	xUrl: field_twitter_url,
	linkedinUrl: field_linkedin_url,
	wheelchairText: field_wheelchair_text,
	isVirtualCentre: field_is_virtual_centre,
});
export const SectionRehomingCentreKeyDetails: React.FC<
	SectionRehomingCentreKeyDetailsProps
> = ({
	heading,
	addressLineOne,
	addressLineTwo,
	city,
	county,
	postcode,
	openingTimesText,
	phoneNumber,
	googleMapsLink,
	facebookUrl,
	instagramUrl,
	xUrl,
	linkedinUrl,
	wheelchairText,
	isVirtualCentre,
}) => {
	return (
		<WrappedSection
			sectionName="SectionRehomingCentreKeyDetails"
			sectionColour={Colours.SANDY_FUR}
		>
			<div className={styles.rehomingcentredetailscolumninner}>
				<div className={styles.titlewrapper}>
					<h2 className={styles.title}>Key information</h2>
				</div>
				<div className={styles.details}>
					<div className={styles.detailssection}>
						<h3>Location</h3>
						<div className={styles.detailssectionitem}>
							<div className={styles.iconcolumn}>
								<Location height={"22px"} width={"22px"} />
							</div>
							<address className={styles.centreaddress}>
								{`Dogs Trust ${heading}`}
								<br />
								{!!addressLineOne && (
									<>
										{`${addressLineOne}`}
										<br />
									</>
								)}
								{addressLineTwo?.trim()?.length > 0 && (
									<>
										{`${addressLineTwo}`}
										<br />
									</>
								)}
								{city?.trim()?.length > 0 && (
									<>
										{`${city}`} <br />
									</>
								)}
								{county?.trim()?.length > 0 && (
									<>
										{`${county}`}
										<br />
									</>
								)}
								{postcode?.trim()?.length > 0 && (
									<>
										{`${postcode}`}
										<br />
									</>
								)}
								{googleMapsLink?.trim()?.length > 0 && (
									<a
										className={styles.googlemaps}
										href={googleMapsLink}
										target="__blank"
									>
										Google Maps
									</a>
								)}
							</address>
						</div>
					</div>
					<div className={styles.detailssection}>
						<h3>Opening hours</h3>
						<div className={styles.detailssectionitem}>
							<div className={styles.iconcolumn}>
								<Clock height={"22px"} width={"22px"} />
							</div>
							<p className={styles.contenttext}>
								{openingTimesText || DEFAULT_OPENING_TIME_TEXT}
							</p>
						</div>
					</div>
					{!isVirtualCentre && (
						<div className={styles.detailssection}>
							<h3>Accessibility</h3>
							<div className={styles.detailssectionitem}>
								<div className={styles.iconcolumn}>
									<Wheelchair height={"22px"} width={"22px"} />
								</div>
								<div className={styles.contentcolumn}>
									<h4 className={styles.contenttitle}>Wheelchair access</h4>
									<p className={styles.contenttext}>
										{wheelchairText || DEFAULT_WHEELCHAIR_TEXT}
									</p>
								</div>
							</div>
							<div className={styles.detailssectionitem}>
								<div className={styles.iconcolumn}>
									<HearingLoop height={"22px"} width={"22px"} />
								</div>
								<div className={styles.contentcolumn}>
									<h4 className={styles.contenttitle}>Hearing loop</h4>
									<p className={styles.contenttext}>{DEFAULT_HEARING_TEXT}</p>
								</div>
							</div>
						</div>
					)}
					<div className={styles.detailssection}>
						<h3>Contact</h3>
						<div className={styles.detailssectionitem}>
							<div>
								<Phone height="22px" width="22px" />
							</div>
							<div className={styles.link}>
								<a
									href={`tel:${phoneNumber?.replace(/\s/g, "") || "02078370006"}`}
								>
									{phoneNumber || "020 7837 0006"}
								</a>
							</div>
						</div>
						<div className={styles.detailssectionitem}>
							<div />
							<div>
								<p className={styles.contenttext}>
									{DEFAULT_PHONE_LINES_TEXT_L1}
								</p>
							</div>
						</div>
						<div className={styles.detailssectionitem}>
							<div>
								<Envelope height="22px" width="22px" />
							</div>
							<div className={styles.link}>
								<a href="mailto:info@dogstrust.org.uk">info@dogstrust.org.uk</a>
							</div>
						</div>

						<div className={styles.detailssectionitem}>
							<div />
							<div className={styles.followus}>
								{(xUrl || facebookUrl || instagramUrl || linkedinUrl) && (
									<h4 className={styles.itemtitle}>Follow us</h4>
								)}
								<SocialMediaIcons
									className={styles.socmedicons}
									invert={true}
									iconSize={"35px"}
									xUrl={xUrl}
									facebookUrl={facebookUrl}
									instagramUrl={instagramUrl}
									linkedInUrl={linkedinUrl}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</WrappedSection>
	);
};

export default SectionRehomingCentreKeyDetails;
